import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Button, DropdownMenu, Input, Label, SparkIcon, Spinner, Text, TooltipTrigger } from '@spark';
import {
    TRAJECTORY_ROLE_TARGETS,
    assignRoleValues,
    defaultTrajectoryKeys,
    messageKeySamples,
    messageKeysFromRows,
    messagesExampleSamples,
    roleTargetsView,
    toolCallArgumentsLookInvalid,
    toolCallFieldSamples,
    toolCallKeySamples,
    toolCallKeysDetails,
} from './trajectoryMapping';
import { useTrajectoryRoleScan } from './useTrajectoryRoleScan';

export const MAP_TRAJECTORY_FIELDS_TITLE = 'Map Trajectory Fields';
export const MAP_TRAJECTORY_FIELDS_SUBTITLE =
    'Pick the field that holds each conversation, then the keys inside every message';

export const MESSAGES_FIELD_LABEL = 'Select Trajectory Messages Field';
export const ROLE_FIELD_LABEL = 'Select Role Field';
export const CONTENT_FIELD_LABEL = 'Select Content Field';
export const TOOL_CALL_FIELD_LABEL = 'Select Tool Call Field';
export const MAP_ROLES_TITLE = 'Map Roles';
export const MAP_TOOL_CALL_KEYS_TITLE = 'Map Tool Call Keys';
/** Tool call is optional: the first item of its select unsets it. */
export const NO_TOOL_CALL_OPTION = 'None';
const TOOL_ARGUMENTS_WARNING =
    'Tool arguments are expected to be an object; some values under this key are plain text';

/** Keys of the `Map Tool Call Keys` rows (left disabled input → details field). */
export const TOOL_CALL_KEY_ROWS = [
    { key: 'name', field: 'trajectoryToolCallNameKey', title: 'Tool Name' },
    { key: 'arguments', field: 'trajectoryToolCallArgumentsKey', title: 'Tool Arguments' },
];

/** Width of the elbow connector between a field and its nested rows (Figma vectors). */
const ELBOW_WIDTH = 14;

const selectItems = (options, selected, onSelect) =>
    options.map((option) => ({
        label: option,
        state: option === selected ? 'selected' : 'default',
        onClick: () => onSelect(option),
    }));

const InvalidIcon = () => (
    <span className="cw-dc-traj__invalid-icon" aria-hidden="true">
        <SparkIcon slug="circle-alert-triangle" size="small" />
    </span>
);

const WarningIcon = ({ text }) => (
    <TooltipTrigger text={text}>
        <span className="cw-dc-traj__warning-icon" data-testid="tool-arguments-warning">
            <SparkIcon slug="circle-alert-triangle" size="small" />
        </span>
    </TooltipTrigger>
);

/**
 * Elbow connectors (1px #E2E2E9, Figma vectors) from the bottom of a block's
 * select to the vertical centre of every nested row's first anchor. Measured
 * from the DOM so example cards of any height never break the lines.
 */
function useElbowConnectors(blockRef, anchorRef, childrenRef) {
    const [elbows, setElbows] = useState([]);
    const elbowsRef = useRef(elbows);
    elbowsRef.current = elbows;

    const measureRef = useRef(null);
    measureRef.current = () => {
        const block = blockRef.current;
        const anchor = anchorRef.current;
        const children = childrenRef.current;
        const next = [];
        if (block && anchor && children) {
            const blockRect = block.getBoundingClientRect();
            const anchorRect = anchor.getBoundingClientRect();
            for (const child of Array.from(children.children)) {
                const target = child.querySelector('[data-tj-anchor]');
                if (!target) continue;
                const rect = target.getBoundingClientRect();
                const height = rect.top + rect.height / 2 - anchorRect.bottom;
                if (!(height > 0) || !(rect.width > 0)) continue;
                next.push({
                    top: Math.round(anchorRect.bottom - blockRect.top),
                    left: Math.round(rect.left - blockRect.left - ELBOW_WIDTH),
                    height: Math.round(height),
                });
            }
        }
        if (JSON.stringify(next) !== JSON.stringify(elbowsRef.current)) setElbows(next);
    };

    // Re-measure after every render (rows appear / disappear) …
    useLayoutEffect(() => {
        measureRef.current();
    });

    // … and whenever the block itself is resized (cards wrap, window resize).
    useEffect(() => {
        const block = blockRef.current;
        if (!block || typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(() => measureRef.current());
        observer.observe(block);
        return () => observer.disconnect();
    }, [blockRef]);

    return elbows;
}

/**
 * One node of the mapper tree: `[header] field card` on a row, nested rows
 * below it (Figma 4012:19043). `header` is only given on the root row; nested
 * rows are indented under their parent's select with elbow connectors.
 */
const TreeBlock = ({ level, header, field, card, children, testId }) => {
    const blockRef = useRef(null);
    const anchorRef = useRef(null);
    const childrenRef = useRef(null);
    const elbows = useElbowConnectors(blockRef, anchorRef, childrenRef);
    return (
        <div className={`cw-tj-block cw-tj-block--l${level}`} ref={blockRef} data-testid={testId}>
            <div className="cw-tj-block__row">
                {header}
                <div className="cw-tj-block__field" ref={anchorRef} data-tj-parent-anchor>
                    {field}
                </div>
                {card}
            </div>
            {children ? (
                <div className="cw-tj-block__children" ref={childrenRef}>
                    {children}
                </div>
            ) : null}
            {elbows.map((elbow, index) => (
                <span
                    key={index}
                    className="cw-tj-elbow"
                    aria-hidden="true"
                    style={{
                        top: elbow.top,
                        left: elbow.left,
                        height: elbow.height,
                        width: ELBOW_WIDTH,
                    }}
                />
            ))}
        </div>
    );
};

/** Labelled single select over file columns / message keys (Figma "Input Single Select"). */
const FieldSelect = ({
    label,
    rowKey,
    value,
    options,
    placeholder = 'Select',
    disabled = false,
    error,
    warning,
    onSelect,
    className = '',
}) => (
    <div
        className={`cw-tj-field${className ? ` ${className}` : ''}`}
        data-trajectory-row={rowKey}
    >
        {label ? <Label label={label} showAsterisk={false} showIcon={false} /> : null}
        <div
            className={`cw-tj-field__select${error ? ' cw-dc-traj__select--invalid' : ''}`}
            data-tj-anchor
        >
            <DropdownMenu
                selectType="single-select"
                size="medium"
                fill
                showLabel={false}
                panelLayout="overlay"
                placeholder={placeholder}
                text={value || undefined}
                disabled={disabled}
                prefixIcon={
                    error ? <InvalidIcon /> : warning ? <WarningIcon text={warning} /> : undefined
                }
                items={selectItems(options, value, onSelect)}
            />
        </div>
        {error ? (
            <div className="cw-field-error" role="alert">
                {error}
            </div>
        ) : null}
    </div>
);

/** Read-only left cell of the Map Roles / Map Tool Call Keys rows. */
const KeyInput = ({ label, value, ariaLabel, className = '' }) => (
    <div className={`cw-tj-field${className ? ` ${className}` : ''}`}>
        {label ? <Label label={label} showAsterisk={false} showIcon={false} /> : null}
        <div data-tj-anchor>
            <Input
                size="medium"
                showLabel={false}
                showHelperText={false}
                disabled
                value={value}
                readOnly
                aria-label={ariaLabel}
                className="cw-tj-key-input"
            />
        </div>
    </div>
);

/**
 * Light-blue example card next to a field (Figma 4022:13056 / 4022:13093):
 * `<Title>:` + one sample, shuffle to see another; the Messages card also has
 * an eye toggle that shows the whole sample instead of the 3-line clamp.
 */
const ExampleCard = ({ title, samples, expandable = false, className = '', testId }) => {
    const [index, setIndex] = useState(0);
    const [expanded, setExpanded] = useState(false);
    useEffect(() => {
        setIndex(0);
    }, [samples]);
    if (!samples.length) return null;
    const sample = samples[Math.min(index, samples.length - 1)];
    const shuffle = () => {
        if (samples.length < 2) return;
        setIndex((current) => {
            let next = Math.floor(Math.random() * samples.length);
            if (next === current) next = (current + 1) % samples.length;
            return next;
        });
    };
    const lowerTitle = title.toLowerCase();
    return (
        <div
            className={`cw-tj-card${expanded ? ' cw-tj-card--expanded' : ''}${
                className ? ` ${className}` : ''
            }`}
            data-testid={testId}
        >
            <div className="cw-tj-card__body">
                <Text variant="base-medium" color="primary" as="p" className="cw-tj-card__title">
                    {title}:
                </Text>
                <Text variant="detail-regular" color="primary" as="p" className="cw-tj-card__text">
                    {sample}
                </Text>
            </div>
            <div className="cw-tj-card__actions">
                {/* Figma Button Icon on the card is the ghost variant. */}
                {expandable ? (
                    <Button
                        size="tiny"
                        variant="ghost"
                        icon={<SparkIcon slug={expanded ? 'eye-closed' : 'eye'} size="small" />}
                        iconSlug={expanded ? 'eye-closed' : 'eye'}
                        aria-label={
                            expanded ? `Show less of the ${lowerTitle} example` : `Show the full ${lowerTitle} example`
                        }
                        aria-pressed={expanded}
                        onClick={() => setExpanded((current) => !current)}
                    />
                ) : null}
                <Button
                    size="tiny"
                    variant="ghost"
                    icon={<SparkIcon slug="arrows-shuffle" size="small" />}
                    iconSlug="arrows-shuffle"
                    aria-label={`Shuffle ${lowerTitle} example`}
                    tooltip="Show a different example"
                    disabled={samples.length < 2}
                    onClick={shuffle}
                />
            </div>
        </div>
    );
};

/**
 * `user | assistant | tool` rows (Figma 4023:23123): the canonical target on
 * the left, a multi-select of the file's role values on the right. A value
 * assigned to one target is not offered to the others.
 */
const MapRolesRows = ({ roleValues, roleMapping, onChangeMapping }) => {
    const view = roleTargetsView(roleValues, roleMapping);
    return TRAJECTORY_ROLE_TARGETS.map((target, index) => {
        const assigned = view[target];
        const options = roleValues.filter(
            (value) => assigned.includes(value) || view.unassigned.includes(value)
        );
        const toggle = (value) => {
            const next = assigned.includes(value)
                ? assigned.filter((item) => item !== value)
                : [...assigned, value];
            onChangeMapping(assignRoleValues(roleMapping, target, next));
        };
        return (
            <div
                className={`cw-tj-map-row cw-tj-map-row--role${index === 0 ? ' cw-tj-map-row--first' : ''}`}
                data-role-target={target}
                key={target}
            >
                <KeyInput
                    label={index === 0 ? MAP_ROLES_TITLE : undefined}
                    value={target}
                    ariaLabel={`${target} role`}
                    className="cw-tj-map-row__key"
                />
                <div className="cw-tj-field cw-tj-map-row__value">
                    <div className="cw-tj-field__select">
                        <DropdownMenu
                            selectType="multi-select"
                            size="medium"
                            fill
                            showLabel={false}
                            panelLayout="overlay"
                            placeholder="Select"
                            tags={assigned}
                            onTagRemove={(tag) => toggle(tag)}
                            disabled={!options.length}
                            items={options.map((value) => ({
                                label: value,
                                checked: assigned.includes(value),
                                onClick: () => toggle(value),
                            }))}
                        />
                    </div>
                </div>
            </div>
        );
    });
};

/**
 * "Map Trajectory Fields" block of the wizard Data Collection step (Figma
 * 4012:19043 / 4023:23405). A progressive tree: the messages field reveals the
 * role / content / tool-call fields, the role field reveals Map Roles and the
 * tool-call field reveals Map Tool Call Keys; every field shows an example
 * card from the picked file.
 */
const TrajectoryFieldsMapper = ({
    slot,
    details,
    columns = [],
    columnsLoading = false,
    errors = {},
    onPatch,
}) => {
    const { sampleRows } = useTrajectoryRoleScan({
        slot,
        details,
        onPatch,
        enabled: Boolean(details?.hasTrajectories),
    });

    const column = details.trajectoryColumn || null;
    const roleKey = details.trajectoryRoleKey || null;
    const contentKey = details.trajectoryContentKey || null;
    const toolCallKey = details.trajectoryToolCallKey || null;
    const nameKey = details.trajectoryToolCallNameKey || null;
    const argumentsKey = details.trajectoryToolCallArgumentsKey || null;
    const messageKeys = details.trajectoryMessageKeys || [];
    const toolCallKeys = details.trajectoryToolCallKeys || [];
    const roleValues = details.trajectoryRoleValues || [];
    const roleMapping = details.trajectoryRoleMapping || {};

    // Always offer the current pick so a hydrated draft renders even before
    // the file's keys are known again.
    const withCurrent = (options, current) =>
        current && !options.includes(current) ? [current, ...options] : options;
    const columnOptions = withCurrent(columns, column);
    const messageKeyOptions = (current) => withCurrent(messageKeys, current);
    const toolCallKeyOptions = (current) => withCurrent(toolCallKeys, current);

    const messagesSamples = useMemo(
        () => messagesExampleSamples(sampleRows, column),
        [sampleRows, column]
    );
    const roleSamples = useMemo(
        () => messageKeySamples(sampleRows, column, roleKey),
        [sampleRows, column, roleKey]
    );
    const contentSamples = useMemo(
        () => messageKeySamples(sampleRows, column, contentKey),
        [sampleRows, column, contentKey]
    );
    const toolSamples = useMemo(
        () => toolCallFieldSamples(sampleRows, column, toolCallKey),
        [sampleRows, column, toolCallKey]
    );
    const toolNameSamples = useMemo(
        () => toolCallKeySamples(sampleRows, column, toolCallKey, nameKey),
        [sampleRows, column, toolCallKey, nameKey]
    );
    const toolArgumentsSamples = useMemo(
        () => toolCallKeySamples(sampleRows, column, toolCallKey, argumentsKey),
        [sampleRows, column, toolCallKey, argumentsKey]
    );

    const selectColumn = (option) => {
        // A new messages column invalidates the message keys, role values and
        // tool-call keys; re-default role / content / tool_call from its sample.
        const keys = messageKeysFromRows(sampleRows, option);
        const defaults = defaultTrajectoryKeys(keys);
        onPatch({
            trajectoryColumn: option,
            trajectoryMessageKeys: keys,
            trajectoryRoleValues: [],
            trajectoryRoleMapping: {},
            trajectoryError: null,
            ...defaults,
            ...toolCallKeysDetails(sampleRows, option, defaults.trajectoryToolCallKey),
        });
    };

    const selectRoleKey = (option) =>
        onPatch({
            trajectoryRoleKey: option,
            trajectoryRoleValues: [],
            trajectoryRoleMapping: {},
            trajectoryError: null,
        });

    const selectToolCallKey = (option) => {
        const next = option === NO_TOOL_CALL_OPTION ? '' : option;
        onPatch({
            trajectoryToolCallKey: next,
            ...toolCallKeysDetails(sampleRows, column, next),
        });
    };

    const selectToolCallSubKey = (row, option) =>
        onPatch({ [row.field]: option === NO_TOOL_CALL_OPTION ? null : option });

    const roleError =
        !details.trajectoryRolesLoading && (details.trajectoryError || errors.trajectoryRoleMapping);

    const header = (
        <div className="cw-tj-header">
            <h3 className="cw-dc-section-title">{MAP_TRAJECTORY_FIELDS_TITLE}</h3>
            <p className="cw-dc-field-row__desc">{MAP_TRAJECTORY_FIELDS_SUBTITLE}</p>
        </div>
    );

    const roleBlock = (
        <TreeBlock
            level={1}
            testId="trajectory-role-block"
            field={
                <FieldSelect
                    label={ROLE_FIELD_LABEL}
                    rowKey="role"
                    value={roleKey || ''}
                    options={messageKeyOptions(roleKey)}
                    error={errors.trajectoryRoleKey}
                    onSelect={selectRoleKey}
                />
            }
            card={<ExampleCard title="Role" samples={roleSamples} testId="role-example-card" />}
        >
            {roleKey ? (
                <>
                    <MapRolesRows
                        roleValues={roleValues}
                        roleMapping={roleMapping}
                        onChangeMapping={(next) => onPatch({ trajectoryRoleMapping: next })}
                    />
                    {details.trajectoryRolesLoading ? (
                        <div className="cw-tj-status" data-testid="trajectory-role-scan-status">
                            <Spinner size="small" />
                            <Text variant="detail-regular" color="secondary" as="span">
                                Scanning the file for role values…
                            </Text>
                        </div>
                    ) : null}
                    {roleError ? (
                        <div className="cw-field-error cw-tj-status" role="alert">
                            {roleError}
                        </div>
                    ) : null}
                </>
            ) : null}
        </TreeBlock>
    );

    const contentBlock = (
        <TreeBlock
            level={1}
            testId="trajectory-content-block"
            field={
                <FieldSelect
                    label={CONTENT_FIELD_LABEL}
                    rowKey="content"
                    value={contentKey || ''}
                    options={messageKeyOptions(contentKey)}
                    error={errors.trajectoryContentKey}
                    onSelect={(option) => onPatch({ trajectoryContentKey: option })}
                />
            }
            card={<ExampleCard title="Content" samples={contentSamples} testId="content-example-card" />}
        />
    );

    const toolCallKeysRows = toolCallKey && toolCallKeys.length ? (
        TOOL_CALL_KEY_ROWS.map((row, index) => {
            const value = details[row.field] || null;
            const samples = row.key === 'name' ? toolNameSamples : toolArgumentsSamples;
            const warning =
                row.key === 'arguments' && toolCallArgumentsLookInvalid(samples)
                    ? TOOL_ARGUMENTS_WARNING
                    : undefined;
            const options =
                row.key === 'arguments'
                    ? [NO_TOOL_CALL_OPTION, ...toolCallKeyOptions(value)]
                    : toolCallKeyOptions(value);
            return (
                <div
                    className={`cw-tj-map-row cw-tj-map-row--tool${index === 0 ? ' cw-tj-map-row--first' : ''}`}
                    data-tool-call-key-row={row.key}
                    key={row.key}
                >
                    <KeyInput
                        label={index === 0 ? MAP_TOOL_CALL_KEYS_TITLE : undefined}
                        value={row.key}
                        ariaLabel={`tool ${row.key} key`}
                        className="cw-tj-map-row__key cw-tj-map-row__key--tool"
                    />
                    <div className="cw-tj-map-row__tool-value">
                        <FieldSelect
                            rowKey={`tool_${row.key}`}
                            value={value || ''}
                            options={options}
                            error={errors[row.field]}
                            warning={warning}
                            onSelect={(option) => selectToolCallSubKey(row, option)}
                            className="cw-tj-map-row__tool-select"
                        />
                        <ExampleCard
                            title={row.title}
                            samples={samples.map((sample) => sample.text)}
                            className="cw-tj-card--tool"
                            testId={`tool-${row.key}-example-card`}
                        />
                    </div>
                </div>
            );
        })
    ) : null;

    const toolCallBlock = (
        <TreeBlock
            level={1}
            testId="trajectory-tool-call-block"
            field={
                <FieldSelect
                    label={TOOL_CALL_FIELD_LABEL}
                    rowKey="tool_call"
                    value={toolCallKey || ''}
                    placeholder="Select (optional)"
                    options={[NO_TOOL_CALL_OPTION, ...messageKeyOptions(toolCallKey)]}
                    onSelect={selectToolCallKey}
                />
            }
            card={<ExampleCard title="Tool" samples={toolSamples} testId="tool-example-card" />}
        >
            {toolCallKeysRows}
        </TreeBlock>
    );

    return (
        <div className="cw-dc-traj cw-tj" data-testid="trajectory-fields-mapper">
            <TreeBlock
                level={0}
                header={header}
                field={
                    <FieldSelect
                        label={MESSAGES_FIELD_LABEL}
                        rowKey="messages"
                        value={column || ''}
                        options={columnOptions}
                        disabled={columnsLoading}
                        error={errors.trajectoryColumn}
                        onSelect={selectColumn}
                    />
                }
                card={
                    <ExampleCard
                        title="Messages"
                        samples={messagesSamples}
                        expandable
                        testId="messages-example-card"
                    />
                }
            >
                {column ? (
                    <>
                        {roleBlock}
                        {contentBlock}
                        {toolCallBlock}
                    </>
                ) : null}
            </TreeBlock>
        </div>
    );
};

export default TrajectoryFieldsMapper;
